import type * as Contentful from 'contentful'
import clsx from 'clsx'
import { contentfulImage } from '~/utils/contentful-image'

enum AssetTags {
  Screenshot = 'screenshot',
  LowResolution = 'screenshotLowResolution',
  HasBrowserChrome = 'screenshotHasOSChrome',
}

// 50rem = 800px
const contentColumnWidth = 50 * 16

type FigureProps = Contentful.Asset & { dimensions?: boolean }

const getImage = (
  metadata: Contentful.Asset['metadata'],
  fields: Contentful.Asset['fields'],
) => {
  if (!fields || !('file' in fields))
    throw new Error('Asset does not have file')
  if (fields.file === undefined) throw new Error('Asset does not have file')
  if (!('details' in fields.file) || fields.file.details === undefined)
    throw new Error('Received AssetFields instead of AssetFile')
  if (
    !('image' in fields.file.details) ||
    fields.file.details.image === undefined
  )
    throw new Error('Received Asset without image details')

  const tags = metadata.tags.map((tag) => tag.sys.id)
  const isLowResolution = tags.includes(AssetTags.LowResolution)

  const { height, width } = fields.file.details.image
  const aspectRatio = height / width
  const maxAvailableResolution = isLowResolution ? Math.round(width / 2) : width
  const computedWidth = Math.min(maxAvailableResolution, contentColumnWidth)
  const computedHeight = Math.round(computedWidth * aspectRatio)

  return {
    alt: typeof fields.title === 'string' ? fields.title : '',
    computedHeight,
    computedWidth,
    src: contentfulImage(
      typeof fields.file.url === 'string' ? fields.file.url : undefined,
      { w: computedWidth * 2 },
    ),
    tags,
  }
}

export const Figure = ({
  metadata,
  fields,
  dimensions = true,
}: FigureProps) => {
  let image: ReturnType<typeof getImage>
  try {
    image = getImage(metadata, fields)
  } catch (error) {
    return (
      <figure className="image">
        <figcaption>
          {error instanceof Error ? error.message : 'Could not load image'}
        </figcaption>
      </figure>
    )
  }

  const dimensionAttributes = dimensions
    ? { height: image.computedHeight, width: image.computedWidth }
    : {}

  return (
    <figure className="image">
      <div className="figure-inner">
        <img
          {...dimensionAttributes}
          alt={image.alt}
          className={clsx('image', image.tags)}
          src={image.src}
        />
      </div>
      {typeof fields?.description === 'string' && (
        <figcaption>{fields.description}</figcaption>
      )}
    </figure>
  )
}
